import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  ProductType, ThemeLineItemType, ThemeReplacementTag
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ProductService, ThemeService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'product-replacement-tags',
  templateUrl: './product-replacement-tags.component.html',
  styleUrls: ['./product-replacement-tags.component.less']
})

export class ProductReplacementTags extends SituBaseComponent {

  themeReplacementTags?: ThemeReplacementTag[];

  tagControl = new FormControl();
  filteredTags: Observable<ProductType[]> | undefined;
  selectedThemeReplacementTag?: ThemeReplacementTag;

  isNew = false;

  private _loading = 0;
  // The page is only hidden for the first load, so later reloads keep selections, open panels and scroll position
  hasLoaded = false;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    if (val == 0) {
      this.hasLoaded = true;
    }
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService,
    private themeService: ThemeService,) {
    super(authService, stateService);

    this.getThemeReplacementTags();
    this.getProductTypes();
    this.getThemeLineItemTypes();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.filteredTags = this.tagControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterTags(value))
    );

    this.filteredProductTypes = this.productTypesControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterProductTypes(value);
      })
    );

    this.filteredThemeLineItemTypes = this.themeLineItemTypesControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this.filterThemeLineItemTypes(value);
      })
    );

    this.filteredEditThemeLineItemTypes = this.editThemeLineItemTypeControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterThemeLineItemTypes(value))
    );
  }

  async getThemeReplacementTags() {
    try {
      this.loading++;
      var themeReplacementTags = await this.productService.LoadThemeReplacementTags(true, true);
      
      if (themeReplacementTags.length == 0) {
        this.snackBar.open("Failed to load Theme Replacement Tags.");
      }

      this.themeReplacementTags = themeReplacementTags;

      this.tagControl.setValue('');
    } catch (e) {
      this.snackBar.open("Failed to load Theme Replacement Tags.");
    } finally {
      this.loading--;
    }
  }

  createProductReplacementTag() {
    this.isNew = true;

    var newTag = new ThemeReplacementTag();
    newTag.id = Guid.create().toString();
    newTag.tag = "New Tag";
    newTag.productTypeIds = [];
    newTag.themeLineItemTypeIds = [];

    this.selectedThemeReplacementTag = newTag;
  }

  onClearTag() {
    this.clearTag();
  }

  clearTag() {
    this.tagControl.reset();
      
    this.tagControl.setValue(undefined);
    this.onTagSelected(undefined);
  }

  displayReplacementTagTag(tag: ThemeReplacementTag | undefined): string {
    return tag?.tag || '';
  }
  
  private filterTags(filter: any = undefined): ThemeReplacementTag[] {
    if (filter instanceof ThemeReplacementTag) {
      return [filter];
    }

    var themeReplacementTags = this.themeReplacementTags!;

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      themeReplacementTags = themeReplacementTags!.filter(x => x.tag).filter(x => x.tag!.toLowerCase().includes(searchTerm!));
    }

    return themeReplacementTags;
  }

  onTagSelected(tag: ThemeReplacementTag | undefined): void {
    this.selectedThemeReplacementTag = tag;

    this.isNew = false;
  }

  async saveProductReplacementTag() {
    if (this.isNew) {
      var response = await this.productService.AddThemeReplacementTag(this.selectedThemeReplacementTag!);

      if (response) {
        this.themeReplacementTags!.unshift(response);
        this.tagControl.setValue(response);
      } else {
        this.snackBar.open("Failed to add Theme Replacement Tags.");
      }
    } else {
      var response = await this.productService.UpdateThemeReplacementTag(this.selectedThemeReplacementTag!);

      if (!response) {
        this.snackBar.open("Failed to update Theme Replacement Tags.");
      }
    }

    this.isNew = false;
  }

  /*  ProductType AutoComplete Filter */
  productTypesControl = new FormControl();
  filteredProductTypes: Observable<ProductType[]> | undefined;
  allProductTypes: ProductType[] = [];
  @ViewChild('productTypeInput') productTypeInput: ElementRef<HTMLInputElement> | undefined;

  async getProductTypes() {
    try {
      this.loading++;
      var productTypes = await this.productService.LoadProductTypes();

      if (productTypes.length == 0) {
        this.snackBar.open("Failed to load Product Types.");
      }

      this.allProductTypes = productTypes;
    } catch (e) {
      this.snackBar.open("Failed to load Product Types.");
    } finally {
      this.loading--;
    }
  }

  onProductTypeSelected(productTypeId: string) {
    var productTypeIds = new Set<string>(this.selectedThemeReplacementTag!.productTypeIds!);
    productTypeIds.add(productTypeId);
    this.selectedThemeReplacementTag!.productTypeIds = Array.from(productTypeIds);

    this.productTypeInput!.nativeElement.value = '';
    this.productTypesControl.setValue(null);
  }

  onRemoveProductType(productTypeId: string) {
    var productTypeIds = new Set<string>(this.selectedThemeReplacementTag!.productTypeIds!);
    productTypeIds.delete(productTypeId);
    this.selectedThemeReplacementTag!.productTypeIds = Array.from(productTypeIds);

    this.productTypeInput!.nativeElement.value = '';
    this.productTypesControl.setValue(null);
  }

  clearProductType() {
    this.productTypesControl.reset();
  }

  private filterProductTypes(filter: any = undefined): ProductType[] {
    if (filter instanceof ProductType) {
      return [filter];
    }

    var productTypes = this.allProductTypes;

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return productTypes;
  }

  getProductType(id: string) : ProductType | undefined {
    return this.allProductTypes?.find(x => x.id === id);
  }

  /*  ProductType AutoComplete Filter */

  /*  ThemeLineItemType AutoComplete Filter */
  themeLineItemTypesControl = new FormControl();
  filteredThemeLineItemTypes: Observable<ThemeLineItemType[]> | undefined;
  allThemeLineItemTypes: ThemeLineItemType[] = [];
  @ViewChild('themeLineItemTypeInput') themeLineItemTypeInput: ElementRef<HTMLInputElement> | undefined;

  async getThemeLineItemTypes() {
    try {
      this.loading++;
      var themeLineItemTypes = await this.themeService.LoadThemeLineItemTypes();

      if (themeLineItemTypes.length == 0) {
        this.snackBar.open("Failed to load Theme Line Item Types.");
      }

      this.allThemeLineItemTypes = themeLineItemTypes;
    } catch (e) {
      this.snackBar.open("Failed to load Theme Line Item Types.");
    } finally {
      this.loading--;
    }
  }

  onThemeLineItemTypeSelected(themeLineItemTypeId: string) {
    var themeLineItemTypeIds = new Set<string>(this.selectedThemeReplacementTag!.themeLineItemTypeIds!);
    themeLineItemTypeIds.add(themeLineItemTypeId);
    this.selectedThemeReplacementTag!.themeLineItemTypeIds = Array.from(themeLineItemTypeIds);

    this.themeLineItemTypeInput!.nativeElement.value = '';
    this.themeLineItemTypesControl.setValue(null);
  }

  onRemoveThemeLineItemType(themeLineItemTypeId: string) {
    var themeLineItemTypeIds = new Set<string>(this.selectedThemeReplacementTag!.themeLineItemTypeIds!);
    themeLineItemTypeIds.delete(themeLineItemTypeId);
    this.selectedThemeReplacementTag!.themeLineItemTypeIds = Array.from(themeLineItemTypeIds);

    this.themeLineItemTypeInput!.nativeElement.value = '';
    this.themeLineItemTypesControl.setValue(null);
  }

  clearThemeLineItemType() {
    this.themeLineItemTypesControl.reset();
  }

  private filterThemeLineItemTypes(filter: any = undefined): ThemeLineItemType[] {
    if (filter instanceof ThemeLineItemType) {
      return [filter];
    }

    var themeLineItemTypes = this.allThemeLineItemTypes;

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      themeLineItemTypes = themeLineItemTypes.filter(x => x.name).filter(theme => theme.name!.toLowerCase().includes(searchTerm!));
    }

    return themeLineItemTypes;
  }

  getThemeLineItemType(id: string) : ThemeLineItemType | undefined {
    return this.allThemeLineItemTypes?.find(x => x.id === id);
  }

  /*  ThemeLineItemType AutoComplete Filter */

  /*  ThemeLineItemType Add / Edit */
  editThemeLineItemTypeControl = new FormControl();
  filteredEditThemeLineItemTypes: Observable<ThemeLineItemType[]> | undefined;
  selectedThemeLineItemType?: ThemeLineItemType;
  isNewThemeLineItemType = false;

  createThemeLineItemType() {
    this.isNewThemeLineItemType = true;

    var newType = new ThemeLineItemType();
    newType.id = Guid.create().toString();
    newType.name = "New Theme Line Item Type";
    newType.templateName = "productidentifierreplacement";
    newType.themeReplacementTagGroup = "Product Identifier Tags";

    this.selectedThemeLineItemType = newType;
  }

  onClearEditThemeLineItemType() {
    this.editThemeLineItemTypeControl.reset();

    this.editThemeLineItemTypeControl.setValue(undefined);
    this.onEditThemeLineItemTypeSelected(undefined);
  }

  displayThemeLineItemTypeName(themeLineItemType: ThemeLineItemType | undefined): string {
    return themeLineItemType?.name || '';
  }

  onEditThemeLineItemTypeSelected(themeLineItemType: ThemeLineItemType | undefined): void {
    // Edit a copy so unsaved changes don't leak into the shared list
    this.selectedThemeLineItemType = themeLineItemType ? new ThemeLineItemType(themeLineItemType) : undefined;

    this.isNewThemeLineItemType = false;
  }

  async saveThemeLineItemType() {
    var response = this.isNewThemeLineItemType
      ? await this.themeService.AddThemeLineItemType(this.selectedThemeLineItemType!)
      : await this.themeService.UpdateThemeLineItemType(this.selectedThemeLineItemType!);

    if (!response) {
      this.snackBar.open(this.isNewThemeLineItemType ? "Failed to add Theme Line Item Type." : "Failed to update Theme Line Item Type.");
      return;
    }

    this.isNewThemeLineItemType = false;

    await this.getThemeLineItemTypes();
    var savedType = this.getThemeLineItemType(response.id!);
    this.editThemeLineItemTypeControl.setValue(savedType);
    this.onEditThemeLineItemTypeSelected(savedType);
  }

  /*  ThemeLineItemType Add / Edit */
}
